import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth";
import { ChevronDropdown, Tooltip } from "..";
import { Link } from "react-router-dom";
import { LogoutTypePicker } from "./Header";
import lrmuitineLogo from "@/assets/images/lrmuitine_logo.png";
import { getRuntimeConfig } from "@/loadConfig";
import ArrowIcon from "@/assets/icons/ArrowIcon";
import IndicatorIcon from "@/assets/icons/IndicatorIcon";
import { useAccessibility } from "@/shared/contexts/AccessibilityContext";
import AccessibilityIcon from "@/assets/icons/AccessibilityIcon";

function MobileHeader({
  onUserMenuToggle,
  onMainMenuToggle,
}: {
  onUserMenuToggle: () => void;
  onMainMenuToggle: () => void;
}) {
  const { t } = useTranslation();
  const auth = useAuth();

  return (
    <div className="flex items-center justify-between px-4 py-2 max-w-screen-2xl mx-auto">
      <div className="flex items-center space-x-3">
        <img className="h-8 w-auto" src={lrmuitineLogo} alt="LR muitinė" />
        <span className="font-bold text-sm">
          <b>{t("header.title")}</b>
        </span>
      </div>

      <div className="flex items-center space-x-2">
        {auth?.user != null && (
          <button
            type="button"
            className="p-2 text-white hover:bg-gray-700 rounded-md transition-colors"
            onClick={onUserMenuToggle}
            aria-label="Toggle profile"
          >
            <UserIcon />
          </button>
        )}
        <button
          type="button"
          className="p-2 text-white hover:bg-gray-700 rounded-md transition-colors"
          onClick={onMainMenuToggle}
          aria-label="Toggle navigation"
        >
          <MenuIcon />
        </button>
      </div>
    </div>
  );
}

function UserInfo() {
  const { t } = useTranslation();
  const auth = useAuth();

  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm text-gray-300">{auth?.user?.firstName}</span>
      <span className="text-sm text-gray-300">{auth?.user?.rin}</span>
      <Tooltip content={t("auth.rinTooltip")}>
        <div className="text-blue-400 cursor-help">
          <InfoIcon />
        </div>
      </Tooltip>
    </div>
  );
}

function UserMenuLinks({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();

  const linkClass =
    "block px-3 py-2 text-sm text-white hover:text-white hover:bg-gray-700 rounded transition-colors";

  return (
    <div className="space-y-2">
      <button
        onClick={() => {
          auth.showRepresentedEntityModal();
          onClose();
        }}
        className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
      >
        {t("navigation.changeServicesRecipient")}
      </button>
      <Link to="/profile" className={linkClass} onClick={onClose}>
        {t("navigation.profile")}
      </Link>
      <Link to="/representatives" className={linkClass} onClick={onClose}>
        {t("navigation.representatives")}
      </Link>
      <Link to="/delegations" className={linkClass} onClick={onClose}>
        {t("navigation.delegationAgreements")}
      </Link>
      <Link to="/evsi" className={linkClass} onClick={onClose}>
        {t("navigation.evsi")}
      </Link>
      <div className="pt-2">
        <LogoutTypePicker variant="navbar" />
      </div>
    </div>
  );
}

function MobileLanguagePicker({
  onLanguageChange,
}: {
  onLanguageChange: (locale: string) => void;
}) {
  const { i18n } = useTranslation();

  const getButtonClass = (language: string) =>
    `px-3 py-1 text-sm rounded transition-colors ${
      i18n.language === language
        ? "bg-gray-600 text-white"
        : "text-white hover:text-white hover:bg-gray-700"
    }`;

  return (
    <div className="flex space-x-2 pb-3 border-b border-gray-700">
      <button
        onClick={() => onLanguageChange("LT")}
        className={getButtonClass("LT")}
      >
        LT
      </button>
      <button
        onClick={() => onLanguageChange("EN")}
        className={getButtonClass("EN")}
      >
        EN
      </button>
    </div>
  );
}

function NavLinks({ isMobile = false }: { isMobile?: boolean }) {
  const { t } = useTranslation();

  const config = getRuntimeConfig<Record<string, string>>();
  const customsUrl = config?.["lt-customs-url"];

  const linkClass = isMobile
    ? "block px-3 py-2 text-sm text-white hover:text-gray-300 rounded transition-colors"
    : "text-sm text-white hover:text-gray-300 transition-colors hover:cursor-pointer";

  const links = [
    {
      href: "https://www.e-tar.lt/portal/lt/legalAct/7f4c04f03e4511f180c9c618618421ed?csrt=17689320514114609973",
      label: t("navigation.termsOfService"),
      external: true,
    },
    {
      href: customsUrl,
      label: t("navigation.lrCustoms"),
      icon: <ArrowIcon size={16} className="ml-2" direction={"up-right"} />,
      external: true,
    },
    {
      href: "/instructions",
      label: t("navigation.userManual"),
      icon: <IndicatorIcon size={16} variant="white" className="ml-2" />,
      external: false,
    },
  ];

  return (
    <>
      {links.map((link, index) => {
        const className = `${linkClass} ${
          link.icon
            ? isMobile
              ? "flex items-center"
              : "inline-flex items-center"
            : ""
        }`;

        if (!link.href) {
          return null;
        }

        if (link.external) {
          return (
            <a
              key={index}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className={className}
            >
              {link.label}
              {link.icon}
            </a>
          );
        }

        return (
          <Link key={index} to={link.href} className={className}>
            {link.label}
            {link.icon}
          </Link>
        );
      })}
    </>
  );
}

function LanguageDropdown({
  currentLanguage,
  onLanguageChange,
}: {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
}) {
  return (
    <ChevronDropdown
      label={currentLanguage.toUpperCase()}
      variant="secondary"
      size="sm"
      className="bg-transparent! border-none! w-14 text-white! hover:text-gray-300! px-3! py-2!"
      options={[
        {
          label: "LT",
          value: "LT",
          onClick: () => onLanguageChange("LT"),
        },
        {
          label: "EN",
          value: "EN",
          onClick: () => onLanguageChange("EN"),
        },
      ]}
    />
  );
}

function UserIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

interface AccessibilityButtonProps {
  className?: string;
}

function AccessibilityButton({ className = "" }: AccessibilityButtonProps) {
  const { toggleHighContrast, highContrast } = useAccessibility();
  const { t } = useTranslation();
  const baseClasses = "flex justify-center items-center";
  const classes = `${baseClasses} ${className}`;

  return (
    <a
      className={classes}
      onClick={toggleHighContrast}
      aria-label={t("accessibility.toggleHighContrast")}
      aria-pressed={highContrast}
      title={t("accessibility.toggleHighContrast")}
    >
      <div
        className={`flex justify-center items-center cursor-pointer rounded-full w-[32px] h-[32px] ${highContrast ? "text-white hover:bg-primary-hover" : "text-white hover:text-gray-300"}`}
      >
        <AccessibilityIcon size={21} className="ml-1 mt-0.5" />
      </div>
    </a>
  );
}

function Navbar() {
  const { i18n, t } = useTranslation();
  const auth = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  function changeLocale(locale: string) {
    i18n.changeLanguage(locale.toLowerCase());
  }

  return (
    <nav className="bg-layout-palette text-white">
      <div className="md:hidden">
        <MobileHeader
          onUserMenuToggle={() => setIsUserMenuOpen(!isUserMenuOpen)}
          onMainMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />

        {auth?.user != null && isUserMenuOpen && (
          <div className="border-t border-gray-700 px-4 py-3 space-y-3">
            <UserInfo />
            <UserMenuLinks onClose={() => setIsUserMenuOpen(false)} />
          </div>
        )}

        {/* Mobile Main Menu */}
        {isMobileMenuOpen && (
          <div className="border-t border-gray-700 px-4 py-3 space-y-2">
            <MobileLanguagePicker onLanguageChange={changeLocale} />
            <NavLinks isMobile />
            <div className="flex items-center justify-between px-3">
              <span className="text-sm">{t("accessibility.highContrast")}</span>
              <AccessibilityButton />
            </div>
          </div>
        )}
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center justify-end max-w-screen-2xl mx-auto px-8 py-2">
        <div className="flex items-center space-x-6">
          <NavLinks />
          <LanguageDropdown
            currentLanguage={i18n.language}
            onLanguageChange={changeLocale}
          />
          <AccessibilityButton />
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
